import { useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import Button from 'components/Button';
import { ReCAPTCHA } from 'components/ReCAPTCHA';
import { useReCAPTCHA } from 'contexts/ReCAPTCHAContext';
import { media } from 'utils/media';
import MailSentState from '../../components/MailSentState';
import { api } from '../../services/apiClient';

interface EmailPayload {
  name: string;
  email: string;
  description: string;
}

export default function FormSection() {
  const [hasSuccessfullySentMail, setHasSuccessfullySentMail] = useState(false);
  const [hasErrored, setHasErrored] = useState(false);
  const { register, handleSubmit, formState } = useForm();
  const { isSubmitSuccessful, isSubmitting, isSubmitted, errors } = formState;
  const { handleVerifyRecaptcha, recaptcha, refreshRecaptcha, setRefreshRecaptcha } = useReCAPTCHA();

  async function onSubmit({ name, email, description }: EmailPayload) {
    try {
      await api.post('/mail', {
        name,
        email,
        subject: 'Mensagem de formulário',
        message: description,
        recaptcha,
      });
    } catch {
      setHasErrored(true);
      return;
    } finally {
      setRefreshRecaptcha(Date.now());
    }

    setHasSuccessfullySentMail(true);
  }

  const isSent = isSubmitSuccessful && isSubmitted;
  const isDisabled = isSubmitting || isSent;
  const isSubmitDisabled = Object.keys(errors).length > 0 || isDisabled;

  if (hasSuccessfullySentMail) {
    return <MailSentState />;
  }

  return (
    <Container>
      <Wrapper>
        <Form onSubmit={handleSubmit(onSubmit)}>
          {hasErrored && <ErrorMessage>Couldn&apos;t send email. Please try again.</ErrorMessage>}
          <InputGroup>
            <InputStack>
              {errors.name && <ErrorMessage>Name is required</ErrorMessage>}
              <Input placeholder="Seu nome" id="name" disabled={isDisabled} {...register('name', { required: true })} />
            </InputStack>
            <InputStack>
              {errors.email && <ErrorMessage>Email is required</ErrorMessage>}
              <Input placeholder="Seu Email" id="email" disabled={isDisabled} {...register('email', { required: true })} />
            </InputStack>
          </InputGroup>
          <InputStack>
            {errors.description && <ErrorMessage>Description is required</ErrorMessage>}
            <Textarea
              as="textarea"
              placeholder="Digite aqui sua mensagem..."
              id="description"
              disabled={isDisabled}
              {...register('description', { required: true })}
            />
          </InputStack>
          <p>
            <ReCAPTCHA onVerify={handleVerifyRecaptcha} refreshReCaptcha={refreshRecaptcha} />
          </p>
          <Button as="button" type="submit" disabled={isSubmitDisabled}>
            Mandar mensagem
          </Button>{' '}
        </Form>
      </Wrapper>
    </Container>
  );
}

const Container = styled.div`
  min-height: calc(99vh - (490px));
  background:#FBF9ED;

  @media (min-width: 970px) {
    width: 60%;
  }
`;

const Wrapper = styled.div`
  flex: 2;
`;

const Form = styled.form`
  & > * {
    margin-bottom: 2rem;
  }
`;

const Input = styled.input`
  border: 1px solid rgb(var(--inputBackground));
  background: rgb(var(--inputBackground));
  border-radius: 0.6rem;
  font-size: 1.6rem;
  padding: 1.8rem;
  box-shadow: var(--shadow-md);

  &:focus {
    outline: none;
    box-shadow: var(--shadow-lg);
  }

  @media (max-width: 478px) {
    width: 90vw !important;
  }

  @media (max-width: 768px) {
    width: 95vw;
  }
`;

const InputGroup = styled.div`
  display: flex;
  align-items: center;

  & > *:first-child {
    margin-right: 2rem;
  }

  & > * {
    flex: 1;
  }

  ${media('<=tablet')} {
    flex-direction: column;
    & > *:first-child {
      margin-right: 0rem;
      margin-bottom: 2rem;
    }
  }
`;

const InputStack = styled.div`
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
  background: #ffffff;
  border: 1px solid #d9d9d9;
  border-radius: 15px;
  display: flex;
  flex-direction: column;

  & > *:not(:first-child) {
    margin-top: 0.5rem;
  }
`;

const ErrorMessage = styled.p`
  color: rgb(var(--errorColor));
  font-size: 1.5rem;
`;

const Textarea = styled.input`
  border: 1px solid rgb(var(--inputBackground));
  background: rgb(var(--inputBackground));
  border-radius: 0.6rem;
  font-size: 1.6rem;
  padding: 1.8rem;
  box-shadow: var(--shadow-md);

  &:focus {
    outline: none;
    box-shadow: var(--shadow-lg);
  }
  
  min-height: 20rem;

  /* ${media('=tablet')} {
    width: 95vw;
  } */

`;
